import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import PropTypes from "prop-types";

import {
  fetchCart,
  fetchPendingCarts,
  setCartMode,
  addCartItem,
  decrementCartItem,
  removeCartItem,
  clearCart as apiClearCart,
  checkoutCart,
  recallPendingCart,
  deletePendingCart,
  transferCart,
  openCashSession,
  closeCashSession,
} from "../services/cartApi";

import { useCompany } from "./CompanyContext";
import { useAdminScope } from "./AdminScopeContext";
import logger from "../utils/logger";

export const CartContext = createContext(null);

const emptyTotals = {
  total_ht: 0,
  total_vat: 0,
  total_ttc: 0,
};

const initialState = {
  items: [],
  mode: null,
  activityUuid: null,
  activityName: null,
  activityType: null,
  totals: emptyTotals,
  pending: [],
  tempCart: [],
  tempTotals: emptyTotals,
  loading: false,
  error: null,
};

function normalizeTotals(data = {}) {
  return {
    total_ht: data.total_ht ?? 0,
    total_vat: data.total_vat ?? 0,
    total_ttc: data.total_ttc ?? 0,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };

    case "FETCH_SUCCESS":
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null,
      };

    case "FETCH_ERROR":
      return {
        ...state,
        loading: false,
        error: action.error || "Erreur panier",
      };

    case "CHANGE_MODE":
      return {
        ...state,
        mode: action.payload?.mode ?? null,
        activityUuid: action.payload?.activityUuid ?? null,
        activityName: action.payload?.activityName ?? null,
        activityType:
          action.payload?.activityType ??
          action.payload?.mode ??
          null,
      };

    case "SET_PENDING":
      return {
        ...state,
        pending: action.payload ?? [],
      };

    case "SET_TEMP":
      return {
        ...state,
        tempCart: action.payload?.items ?? [],
        tempTotals: action.payload?.totals ?? emptyTotals,
      };

    case "CLEAR_CART":
      return {
        ...state,
        items: [],
        totals: emptyTotals,
        loading: false,
        error: null,
      };

    case "CLEAR_TEMP":
      return {
        ...state,
        tempCart: [],
        tempTotals: emptyTotals,
      };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const {
    user: currentUser,
    companyUuid,
    companyName,
  } = useCompany();

  const { selectedCompanyUuid } = useAdminScope();

  const authenticatedUser = currentUser;

  const [state, dispatch] = useReducer(reducer, initialState);
  const [cashSessionUuid, setCashSessionUuid] = useState(null);
  const [cashSessionOpen, setCashSessionOpen] = useState(false);

  const resetCashSessionState = useCallback(() => {
    setCashSessionUuid(null);
    setCashSessionOpen(false);
  }, []);

  const loadCart = useCallback(async () => {
    if (!authenticatedUser) return;

    dispatch({ type: "FETCH_START" });

    try {
      const { data } = await fetchCart({
        companyUuid: selectedCompanyUuid,
      });

      dispatch({
        type: "FETCH_SUCCESS",
        payload: {
          items: data.products ?? data.items ?? [],
          mode: data.mode ?? null,
          activityUuid: data.activity_uuid ?? null,
          activityName: data.activity_name ?? null,
          activityType: data.activity_type ?? data.mode ?? null,
          totals: normalizeTotals(data),
        },
      });
    } catch (err) {
      logger.warn("Erreur fetchCart:", err);
      dispatch({
        type: "FETCH_ERROR",
        error: err.response?.data?.error || err.message,
      });
    }
  }, [authenticatedUser, selectedCompanyUuid]);

  const loadPending = useCallback(async () => {
    if (!authenticatedUser) return;

    try {
      const { data } = await fetchPendingCarts({
        companyUuid: selectedCompanyUuid,
      });

      dispatch({
        type: "SET_PENDING",
        payload: data.pending_carts ?? [],
      });
    } catch (err) {
      logger.warn("Erreur fetchPendingCarts:", err);
    }
  }, [authenticatedUser, selectedCompanyUuid]);

  useEffect(() => {
    if (!authenticatedUser) {
      dispatch({ type: "RESET" });
      resetCashSessionState();
      return;
    }

    loadCart();
    loadPending();
  }, [
    authenticatedUser,
    selectedCompanyUuid,
    loadCart,
    loadPending,
    resetCashSessionState,
  ]);

  const setCurrentPOSMode = useCallback(
    async (newMode, activityUuid = null, activityName = null) => {
      const payload = {
        mode: newMode,
        activityUuid,
        activityName,
        activityType: newMode,
      };

      dispatch({
        type: "CHANGE_MODE",
        payload,
      });

      try {
        if (newMode) {
          await setCartMode(
            {
              mode: newMode,
              activity_uuid: activityUuid,
            },
            {
              companyUuid: selectedCompanyUuid,
            }
          );
        }

        await loadCart();
      } catch (err) {
        logger.error("Erreur setCartMode:", err);
        dispatch({
          type: "FETCH_ERROR",
          error: err.response?.data?.error || err.message,
        });
      }
    },
    [loadCart, selectedCompanyUuid]
  );

  const resetPOSMode = useCallback(() => {
    dispatch({
      type: "CHANGE_MODE",
      payload: {
        mode: null,
        activityUuid: null,
        activityName: null,
        activityType: null,
      },
    });
  }, []);

  const addItem = useCallback(
    async (itemUuid, qty = 1, options = {}) => {
      dispatch({ type: "FETCH_START" });

      try {
        await addCartItem(itemUuid, qty, {
          activity_uuid: options.activityUuid ?? state.activityUuid,
          activity_type: options.activityType ?? state.activityType,
          source:
            options.source ??
            (state.activityUuid ? "activity" : "shop"),
          companyUuid: selectedCompanyUuid,
        });

        await loadCart();
      } catch (err) {
        logger.error("Erreur addCartItem:", err);
        dispatch({
          type: "FETCH_ERROR",
          error: err.response?.data?.error || err.message,
        });
        throw err;
      }
    },
    [
      state.activityUuid,
      state.activityType,
      loadCart,
      selectedCompanyUuid,
    ]
  );

  const decrementItem = useCallback(
    async (itemUuid) => {
      dispatch({ type: "FETCH_START" });

      try {
        await decrementCartItem(itemUuid, {
          companyUuid: selectedCompanyUuid,
        });

        await loadCart();
      } catch (err) {
        logger.error("Erreur decrementCartItem:", err);
        dispatch({
          type: "FETCH_ERROR",
          error: err.response?.data?.error || err.message,
        });
      }
    },
    [loadCart, selectedCompanyUuid]
  );

  const removeItem = useCallback(
    async (itemUuid) => {
      dispatch({ type: "FETCH_START" });

      try {
        await removeCartItem(itemUuid, {
          companyUuid: selectedCompanyUuid,
        });

        await loadCart();
      } catch (err) {
        logger.error("Erreur removeCartItem:", err);
        dispatch({
          type: "FETCH_ERROR",
          error: err.response?.data?.error || err.message,
        });
      }
    },
    [loadCart, selectedCompanyUuid]
  );

  const clearCart = useCallback(async () => {
    dispatch({ type: "FETCH_START" });

    try {
      await apiClearCart({
        companyUuid: selectedCompanyUuid,
      });

      await loadCart();
    } catch (err) {
      logger.error("Erreur clearCart:", err);
      dispatch({
        type: "FETCH_ERROR",
        error: err.response?.data?.error || err.message,
      });
    }
  }, [loadCart, selectedCompanyUuid]);

  const checkout = useCallback(
    async ({
      payments = [],
      customer = null,
      table_number,
      covers,
    } = {}) => {
      dispatch({ type: "FETCH_START" });

      dispatch({
        type: "SET_TEMP",
        payload: {
          items: state.items,
          totals: state.totals,
        },
      });

      try {
        const { data } = await checkoutCart({
          payments,
          table_number,
          covers,
          activity_uuid: state.activityUuid,
          activity_type: state.activityType,
          client_uuid:
            customer?.uuid
            ?? customer?.client_uuid
            ?? null,
          companyUuid: selectedCompanyUuid,
        });

        return data.receipt_uuid;
      } catch (err) {
        dispatch({
          type: "FETCH_ERROR",
          error: err.response?.data?.error || err.message,
        });
        throw err;
      }
    },
    [
      state.items,
      state.totals,
      state.activityUuid,
      state.activityType,
      selectedCompanyUuid,
    ]
  );

  const confirmPayment = useCallback(() => {
    dispatch({ type: "CLEAR_CART" });
    dispatch({ type: "CLEAR_TEMP" });
  }, []);

  const recallCart = useCallback(
    async (uuid) => {
      await recallPendingCart(uuid, {
        companyUuid: selectedCompanyUuid,
      });

      await loadCart();
      await loadPending();
    },
    [loadCart, loadPending, selectedCompanyUuid]
  );

  const deletePending = useCallback(
    async (uuid) => {
      await deletePendingCart(uuid, {
        companyUuid: selectedCompanyUuid,
      });

      dispatch({
        type: "SET_PENDING",
        payload: state.pending.filter((cart) => cart.uuid !== uuid),
      });
    },
    [state.pending, selectedCompanyUuid]
  );

  const transferTable = useCallback(
    async (fromUuid, toTableId) => {
      await transferCart({ fromUuid, toTableId });
      await loadPending();
    },
    [loadPending]
  );

  const openSession = useCallback(async () => {
    try {
      const { data } = await openCashSession(
        {
          mode: state.mode,
          activity_uuid: state.activityUuid,
          activity_type: state.activityType,
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      setCashSessionUuid(data.cash_session_uuid);
      setCashSessionOpen(true);

      return data;
    } catch (err) {
      logger.error("Erreur ouverture session caisse:", err);
      throw err;
    }
  }, [
    state.mode,
    state.activityUuid,
    state.activityType,
    selectedCompanyUuid,
  ]);

  const closeSession = useCallback(async () => {
    if (!cashSessionUuid) return null;

    try {
      const { data } = await closeCashSession(cashSessionUuid, {
        companyUuid: selectedCompanyUuid,
      });

      setCashSessionOpen(false);
      setCashSessionUuid(null);

      return data;
    } catch (err) {
      logger.error("Erreur fermeture session caisse:", err);
      throw err;
    }
  }, [cashSessionUuid, selectedCompanyUuid]);

  const value = useMemo(
    () => ({
      ...state,

      companyUuid,
      companyName,

      pendingCarts: state.pending,

      currentPOSMode: state.mode,
      currentActivityUuid: state.activityUuid,
      currentActivityName: state.activityName,
      currentActivityType: state.activityType,

      setCurrentPOSMode,
      resetPOSMode,

      addItem,
      decrementItem,
      removeItem,
      clearCart,

      checkout,
      confirmPayment,

      loadCart,
      loadPending,
      recallCart,
      deletePending,
      transferTable,

      cashSessionUuid,
      cashSessionOpen,
      openSession,
      closeSession,
    }),
    [
      state,
      companyUuid,
      companyName,
      setCurrentPOSMode,
      resetPOSMode,
      addItem,
      decrementItem,
      removeItem,
      clearCart,
      checkout,
      confirmPayment,
      loadCart,
      loadPending,
      recallCart,
      deletePending,
      transferTable,
      cashSessionUuid,
      cashSessionOpen,
      openSession,
      closeSession,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

CartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useCart() {
  const ctx = useContext(CartContext);

  if (!ctx) {
    throw new Error("useCart must be used within a <CartProvider>");
  }

  return ctx;
}